import { Plus, X, Search, FilePlus, Video, GripVertical, ChevronDown } from 'lucide-react';
import CreatePtSessionModal from "./CreatePtSessionModal";
import { useContext, useEffect, useState } from 'react';
import { ProgramContext } from '../../context/ProgramsContext';
import toast from 'react-hot-toast';

const ActivityPickerModal = ({ dayId, onClose, onAddActivity, problemArea, problemAreaData, activityLibrary, onOpenViewer }) => {
    const {
        ACTIVITY_TYPES,
        generateUniqueId,
        headerData,
        exerciseTemplates
    } = useContext(ProgramContext)
    const [selectedType, setSelectedType] = useState('PT_SESSION');
    const [searchTerm, setSearchTerm] = useState('');
    const [configuredExercises, setConfiguredExercises] = useState([]);
    const [isCreatePtSessionModalOpen, setCreatePtSessionModalOpen] = useState(false);
    const [draggable, setDraggable] = useState(false)
    const [setsInputValues, setSetsInputValues] = useState({});

    useEffect(() => {
        if (selectedType === 'ROM_TEST' && problemAreaData && problemArea) {
            const romExercises = problemAreaData[problemArea]?.romTests.flatMap(t => t.exercises) || [];
            setConfiguredExercises(romExercises
                ?.filter(
                    (e) =>
                        e?.problem_area?.includes(headerData?.problemId) &&
                        e?.is_rom_exercise === 1
                )
                ?.map(ex => ({
                    ...ex,
                    reps: 8,
                    hold: ex.hasHold ? 5 : null,
                    side: ex.hasSide ? (ex.hasSide === 'Leg' ? 'Left Leg' : 'Left Side') : null,
                    instanceId: generateUniqueId(),
                    isSelected: true,
                    set_limb_instruction: ex?.limb_instructions?.length > 0 ? ex?.limb_instructions[0] : ""
                })));
        }
    }, [problemArea, problemAreaData, selectedType, headerData?.subtype, headerData?.problemId]);

    const handleConfigChange = (instanceId, field, value) => {
        setConfiguredExercises((prev) => {
            // Find the set_id for the targeted instance
            const target = prev.find((ex) => ex.instanceId === instanceId);
            if (!target) return prev;

            const targetSetId = target.set_id;

            return prev.map((ex) => {
                if (ex.set_id !== targetSetId) return ex;

                    if (field === 'hold_in_seconds') {
                        return {
                            ...ex,
                            hold_in_seconds: value,
                            hold: value,
                            exercise_data: {
                                ...ex.exercise_data,
                                hold_in_seconds: value,
                                hold: value
                            },
                        };
                    }

                    if (field === 'reps') {
                        return {
                            ...ex,
                            reps: value,
                            exercise_data: {
                                ...ex.exercise_data,
                                reps: value,
                            },
                        };
                    }

                    return {
                        ...ex,
                        [field]: value,
                    };
            });
        });
    };
    const handleAddRomTest = () => { onAddActivity(dayId, { id: generateUniqueId(), type: 'ROM_TEST', title: 'Custom ROM Assessment', exercises: configuredExercises.filter(ex => ex.isSelected) }); onClose(); };
    // const handleRomDragStart = (e, index) => { e.dataTransfer.setData("text/plain", index); };
    // const handleRomDrop = (e, dropIndex) => {
    //     const dragIndex = parseInt(e.dataTransfer.getData("text/plain"), 10);
    //     setConfiguredExercises(prev => { const newExercises = [...prev]; const [draggedItem] = newExercises.splice(dragIndex, 1); newExercises.splice(dropIndex, 0, draggedItem); return newExercises; });
    // };
    const handleRomDragStart = (e, uiIndex) => {
        e.dataTransfer.setData("dragged-ui-index", uiIndex);
    };

    const handleRomDrop = (e, uiDropIndex) => {
        const uiDragIndex = parseInt(e.dataTransfer.getData("dragged-ui-index"), 10);

        // Build the UI list of unique set_id items
        const uiList = configuredExercises.filter(
            (ex, index, self) => index === self.findIndex(e => e.set_id === ex.set_id)
        );

        const dragSetId = uiList[uiDragIndex]?.set_id;
        const dropSetId = uiList[uiDropIndex]?.set_id;
        if (!dragSetId || !dropSetId) return;

        setConfiguredExercises(prev => {
            // Group to drag
            const draggedGroup = prev.filter(item => item.set_id === dragSetId);
            const remaining = prev.filter(item => item.set_id !== dragSetId);

            // Find drop index in remaining by locating first item with dropSetId
            const targetIndex = remaining.findIndex(item => item.set_id === dropSetId);

            const newList = [
                ...remaining.slice(0, targetIndex),
                ...draggedGroup,
                ...remaining.slice(targetIndex),
            ];

            return newList;
        });
    };



    const filteredPtSessions = activityLibrary?.ptSessions.filter(s => s.title.toLowerCase().includes(searchTerm.toLowerCase())) || [];
    const pickerItemClass = "flex justify-between items-center p-3 mb-2 rounded-lg hover:bg-gray-100 cursor-pointer";

    useEffect(() => {
        configuredExercises.forEach((ex) => {
            handleConfigChange(ex.instanceId, 'reps', ex.reps)
            handleConfigChange(ex.instanceId, 'hold_in_seconds', ex.hold_in_seconds)
        });
    }, [configuredExercises?.length]);

    return (<>
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 animate-fade-in-up">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] sm:max-w-4xl p-6 transform transition-all flex flex-col max-h-[90vh]">

                {/* Modal Header */}
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-bold text-gray-800">Add Activity</h2>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200 transition">
                        <X size={24} />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 mb-4 overflow-hidden" style={{ height: '50px', minHeight: '50px' }}>
                    {Object.entries(ACTIVITY_TYPES).map(([key, { name, icon: Icon }]) => (
                        <button
                            key={key}
                            onClick={() => setSelectedType(key)}
                            className={`p-3 font-semibold flex items-center whitespace-nowrap transition ${selectedType === key
                                ? 'text-[#166273] border-b-2 border-[#166273]'
                                : 'text-gray-500'
                                }`}
                        >
                            <Icon size={16} className="mr-2" />
                            {name}
                        </button>
                    ))}
                </div>

                {/* Scrollable Content */}
                <div className="overflow-y-auto pr-2 flex-grow">
                    {selectedType === 'PT_SESSION' && (
                        <div>
                            {/* Search Input */}
                            <div className="relative mb-4">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                                <input
                                    type="text"
                                    placeholder="Search sessions..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full border rounded-md pl-10 py-2 focus:outline-none focus:ring-2 focus:ring-[#166273]/30"
                                />
                            </div>

                            <button
                                onClick={() => setCreatePtSessionModalOpen(true)}
                                className="w-full flex items-center justify-center gap-2 p-3 mb-2 rounded-lg bg-[#166273]/10 text-[#166273] font-semibold hover:bg-[#166273]/15 transition"
                            >
                                <FilePlus size={20} />
                                Create New PT Session
                            </button>

                            {filteredPtSessions.map((s) => (
                                <div key={s.id} onClick={() => onAddActivity(dayId, s)} className={pickerItemClass}>
                                    <span>{s.title}</span>
                                    <Plus />
                                </div>
                            ))}
                        </div>
                    )}

                    {selectedType === 'ROM_TEST' && (
                        <div>
                            {/* {console.log("configuredExercises", configuredExercises)} */}
                            {configuredExercises
                                ?.filter(
                                    (ex, index, self) =>
                                        index === self.findIndex((e) => e.set_id === ex.set_id)
                                )
                                ?.map((ex, index) => {
                                    const sameIdCount = configuredExercises.filter(e => e.set_id === ex.set_id).length;
                                    const inputValue = setsInputValues[ex.instanceId] ?? sameIdCount;
                                    const exerciseTemplateExercise = exerciseTemplates?.find((e) => e?.name === ex?.name)
                                    return (
                                        <div
                                            key={ex.instanceId}
                                            draggable={draggable}
                                            onDragStart={(e) => handleRomDragStart(e, index)}
                                            onDrop={(e) => handleRomDrop(e, index)}
                                            onDragOver={(e) => e.preventDefault()}
                                            className="flex items-center gap-4 p-3 border rounded-lg mb-2 bg-gray-50 min-w-[800px]"
                                        >
                                            <GripVertical
                                                onMouseOver={() => setDraggable(true)}
                                                onMouseOut={() => setDraggable(false)}
                                                className="cursor-grab text-gray-400" />
                                            <input
                                                type="checkbox"
                                                checked={ex.isSelected}
                                                onChange={(e) => handleConfigChange(ex.instanceId, 'isSelected', e.target.checked)}
                                                className="h-5 w-5"
                                            />
                                            <div className="flex-1 font-semibold">{ex.name}</div>

                                            <div className="flex items-center gap-3">

                                                {ex.limb === 'Y' && (
                                                    <div className="relative w-[120px]">
                                                        <select
                                                            value={ex.set_limb_instruction?.name || ''}
                                                            onChange={(e) => {
                                                                const selectedInstruction = ex?.limb_instructions?.find(obj => obj.name === e.target.value);
                                                                handleConfigChange(ex.instanceId, 'set_limb_instruction', selectedInstruction);
                                                            }}
                                                            className="appearance-none border rounded px-2 py-1 pr-8 text-sm w-full"
                                                        >
                                                            {ex?.limb_instructions?.map((instruction, idx) => (
                                                                <option key={idx} value={instruction.name}>
                                                                    {instruction.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none w-4 h-4" />
                                                    </div>
                                                )}

                                                {ex.hasHold && (
                                                    <label className="flex items-center gap-1 text-sm">
                                                        Hold (s):
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            max={3600}
                                                            disabled={!exerciseTemplateExercise?.exercise_data?.hold_in_seconds}
                                                            value={ex.hold_in_seconds}
                                                            onChange={(e) => {
                                                                if (e.target.value >= 0) {
                                                                    handleConfigChange(ex.instanceId, 'hold_in_seconds', parseInt(e.target.value))
                                                                } else {
                                                                    handleConfigChange(ex.instanceId, 'hold_in_seconds', 0)
                                                                }
                                                            }}
                                                            onBlur={(e) => {
                                                                if (e.target.value > 3600) {
                                                                    toast.dismiss()
                                                                    toast.error("Hold cannot be greater than 3600s")
                                                                    handleConfigChange(ex.instanceId, 'hold_in_seconds', parseInt(3600))
                                                                    return;
                                                                }
                                                                if (e.target.value < 0 || !e.target.value) {
                                                                    handleConfigChange(ex.instanceId, 'hold_in_seconds', parseInt(0))
                                                                } else if (e.target.value > 0 && e.target.value < 4) {
                                                                    handleConfigChange(ex.instanceId, 'hold_in_seconds', parseInt(4))
                                                                }
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.target.blur();
                                                                }
                                                            }}
                                                            className="w-16 border rounded px-2 py-1 text-sm"
                                                        />
                                                    </label>
                                                )}

                                                {/* Sets */}
                                                <div className="min-w-0 flex flex-row text-sm gap-2">
                                                    <span className="whitespace-nowrap">Sets:</span>
                                                    <div className="flex items-stretch border text-center rounded w-full overflow-hidden">
                                                        <button
                                                            type="button"
                                                            className="text-white bg-[#166273] disabled:opacity-50 px-2"
                                                            onClick={() => {
                                                                const lastIndex = [...configuredExercises].reverse().findIndex(e => e.set_id === ex.set_id);
                                                                if (lastIndex !== -1) {
                                                                    const removeIndex = configuredExercises.length - 1 - lastIndex;
                                                                    setConfiguredExercises(prev => prev.filter((_, idx) => idx !== removeIndex));
                                                                }
                                                            }}
                                                            disabled={sameIdCount <= 1}
                                                        >-</button>
                                                        <input
                                                            type="number"
                                                            min={1}
                                                            max={10}
                                                            value={inputValue}
                                                            onChange={(e) => {
                                                                const value = e.target.value;
                                                                if (value > 10) {
                                                                    toast.dismiss()
                                                                    toast.error("Sets cannot be greater than 10")
                                                                    return;
                                                                };
                                                                setSetsInputValues(prev => ({ ...prev, [ex.instanceId]: value }));
                                                            }}
                                                            onBlur={(e) => {
                                                                let newCount = parseInt(e.target.value);
                                                                if (isNaN(newCount) || newCount < 1) newCount = 1;
                                                                if (newCount > 10) newCount = 10;

                                                                const diff = newCount - sameIdCount;

                                                                if (diff > 0) {
                                                                    // Add duplicates
                                                                    const duplicates = Array.from({ length: diff }, () => ({
                                                                        ...ex,
                                                                        instanceId: generateUniqueId(),
                                                                    }));
                                                                    // setConfiguredExercises(prev => [...prev, ...duplicates]);

                                                                    setConfiguredExercises(prev => {
                                                                        const newExercises = [...prev];
                                                                        const lastIndex = [...prev]
                                                                            .map((item, i) => ({ ...item, i }))
                                                                            .filter(item => item.set_id === ex.set_id)
                                                                            .map(item => item.i)
                                                                            .pop();

                                                                        const insertAt = lastIndex !== undefined ? lastIndex + 1 : newExercises.length;
                                                                        newExercises.splice(insertAt, 0, ...duplicates);
                                                                        return newExercises;
                                                                    });
                                                                } else if (diff < 0) {
                                                                    // Remove from end
                                                                    const removeCount = Math.abs(diff);
                                                                    setConfiguredExercises(prev => {
                                                                        let count = 0;
                                                                        const reversed = [...prev].reverse(); // reverse to start removing from the end
                                                                        const filteredReversed = reversed.filter(item => {
                                                                            if (item.set_id === ex.set_id && count < removeCount) {
                                                                                count++;
                                                                                return false;
                                                                            }
                                                                            return true;
                                                                        });
                                                                        return filteredReversed.reverse(); // restore original order
                                                                    });
                                                                }

                                                                // Clear the temp input value
                                                                setSetsInputValues(prev => {
                                                                    const newState = { ...prev };
                                                                    delete newState[ex.instanceId];
                                                                    return newState;
                                                                });
                                                            }}

                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.target.blur(); // triggers blur logic
                                                                }
                                                            }}
                                                            className="w-full max-w-[50px] text-center bg-transparent outline-none disabled:opacity-50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                        />
                                                        <button
                                                            type="button"
                                                            className="text-white bg-[#166273] disabled:opacity-30 px-2"
                                                            onClick={() => {
                                                                const duplicate = { ...ex, instanceId: generateUniqueId() };
                                                                // setConfiguredExercises(prev => [...prev, duplicate]);
                                                                setConfiguredExercises(prev => {
                                                                    const newExercises = [...prev];
                                                                    const lastIndex = prev
                                                                        .map((item, i) => ({ ...item, i }))
                                                                        .filter(item => item.set_id === ex.set_id)
                                                                        .map(item => item.i)
                                                                        .pop();

                                                                    const insertAt = lastIndex !== undefined ? lastIndex + 1 : newExercises.length;
                                                                    newExercises.splice(insertAt, 0, duplicate);
                                                                    return newExercises;
                                                                });
                                                            }}
                                                            disabled={sameIdCount >= 10}
                                                        >+</button>
                                                    </div>
                                                </div>

                                                <label className="flex items-center gap-1 text-sm">
                                                    Reps:
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        max={1000}
                                                        value={ex.reps}
                                                        onChange={(e) => handleConfigChange(ex.instanceId, 'reps', parseInt(e.target.value))}
                                                        onBlur={(e) => {
                                                            if (e.target.value > 1000) {
                                                                toast.dismiss()
                                                                toast.error("Reps cannot be greater than 1000")
                                                                handleConfigChange(ex.instanceId, 'reps', parseInt(1000))
                                                                return;
                                                            }
                                                            if (e.target.value < 1 || !e.target.value) {
                                                                handleConfigChange(ex.instanceId, 'reps', parseInt(1))
                                                            }
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.target.blur();
                                                            }
                                                        }}
                                                        className="w-16 border rounded px-2 py-1 text-sm"
                                                    />
                                                </label>

                                                <button
                                                    onClick={() =>
                                                        onOpenViewer({ ...ex, title: ex.name, content: ex.videoUrl, category: 'Video' })
                                                    }
                                                    className="text-[#166273] hover:text-[#166273]/90 transition"
                                                >
                                                    <Video size={20} />
                                                </button>
                                            </div>
                                        </div>
                                    )
                                })}


                            {
                                configuredExercises?.length === 0 &&
                                <div className='text-center text-gray-400'>No ROM Tests</div>
                            }

                            <div className="sticky bottom-0 mt-4 p-2 bg-white">
                                <button
                                    onClick={handleAddRomTest}
                                    disabled={!configuredExercises?.some((e) => e?.isSelected)}
                                    className="w-full px-4 py-3 bg-[#166273] text-white font-semibold rounded-lg shadow-md hover:bg-[#166273]/90 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
                                >
                                    Add Custom ROM Test to Plan
                                </button>
                            </div>
                        </div>
                    )}

                    {selectedType === 'EDUCATION' && (
                        <div>
                            <h3 className="font-semibold mb-2">Articles</h3>
                            {activityLibrary.education
                                .filter((e) => e.category === 'Article')
                                .map((e) => (
                                    <div key={e.id} className={pickerItemClass}>
                                        <span>{e.title}</span>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => onOpenViewer(e)}
                                                className="px-2 py-1 text-xs rounded-md bg-gray-200 text-gray-800"
                                            >
                                                Read
                                            </button>
                                            <button
                                                onClick={() => onAddActivity(dayId, e)}
                                                className="px-2 py-1 text-xs rounded-md bg-[#166273] hover:bg-[#166273]/90 text-white"
                                            >
                                                <Plus />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            {
                                activityLibrary.education?.filter((e) => e.category === 'Article')?.length === 0 &&
                                <div className='text-center text-gray-400'>No Articles</div>
                            }

                            <h3 className="font-semibold my-2">Videos</h3>
                            {activityLibrary.education
                                .filter((e) => e.category === 'Video')
                                .map((e) => (
                                    <div key={e.id} className={pickerItemClass}>
                                        <span>{e.title}</span>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => onOpenViewer(e)}
                                                className="px-2 py-1 text-xs rounded-md bg-gray-200 text-gray-800"
                                            >
                                                Watch
                                            </button>
                                            <button
                                                onClick={() => onAddActivity(dayId, e)}
                                                className="px-2 py-1 text-xs rounded-md bg-[#166273] text-white"
                                            >
                                                <Plus />
                                            </button>
                                        </div>
                                    </div>
                                ))}

                            {
                                activityLibrary.education?.filter((e) => e.category === 'Video')?.length === 0 &&
                                <div className='text-center text-gray-400'>No Videos</div>
                            }
                        </div>
                    )}

                    {selectedType === 'QUESTIONNAIRE' && (
                        <div>
                            {/* <h3 className="font-semibold mb-2">Functional</h3>
                            {problemAreaData[problemArea]?.functionalQuestionnaires.map((q) => (
                                <div key={q.id} onClick={() => onAddActivity(dayId, q)} className={pickerItemClass}>
                                    <span>{q.title}</span>
                                    <Plus />
                                </div>
                            ))} */}

                            <h3 className="font-semibold my-2">General</h3>
                            {activityLibrary.generalQuestionnaires?.map((q) => (
                                <div key={q.id} onClick={() => onAddActivity(dayId, q)} className={pickerItemClass}>
                                    <span>{q.title}</span>
                                    <Plus />
                                </div>
                            ))}


                            {
                                activityLibrary.generalQuestionnaires
                                    ?.filter((e) => {
                                        const allAreas = ["Lower Back", "Shoulder", "Knee", "Thigh", "Hip"];
                                        const selected = Array.isArray(headerData?.problemArea)
                                            ? headerData.problemArea
                                            : [headerData?.problemArea];
                                        const exclude = allAreas.filter((area) => !selected.includes(area));
                                        const title = e?.title?.toLowerCase() || "";
                                        return !exclude.some((area) =>
                                            title.includes(area?.toLowerCase())
                                        );
                                    })?.length === 0 &&
                                <div className='text-center text-gray-400'>No General Questionnaires</div>
                            }
                        </div>
                    )}
                </div>
            </div>
        </div>

        {isCreatePtSessionModalOpen && <CreatePtSessionModal onOpenViewer={onOpenViewer} onClose={() => setCreatePtSessionModalOpen(false)} onSave={(session) => { onAddActivity(dayId, session); onClose(); }} />}
    </>);
};

export default ActivityPickerModal;